<script setup lang="ts">
import { type MediaCenterSubCategory } from "@/helpers/mediaCenter.type";
import { useAppStore } from "@/store/app";
import type { PictureChoiceOption } from "@/types/pictureChoice";
import {
  updatePictureChoiceOptionLabel,
} from "@/util/pictureChoice";
import {
  applyPictureChoiceMediaSelection,
  openPictureChoiceMediaCenter,
  type PictureChoiceMediaSelection,
} from "@/util/pictureChoiceMedia";
import {
  ImagePlusIcon,
  Menu05Icon,
  Trash01Icon,
  XCloseIcon,
} from "@gohighlevel/ghl-icons/24/outline";
import {
  UIButton,
  UIInput,
  UIInputGroup,
  UITooltip,
} from "@gohighlevel/ghl-ui";
import { cloneDeep } from "lodash";
import { computed, type PropType } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
  option: {
    type: Object as PropType<PictureChoiceOption>,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  mediaCenterSubCategory: {
    type: String as PropType<MediaCenterSubCategory>,
    required: true,
  },
  removable: {
    type: Boolean,
    default: true,
  },
  isOther: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  (
    event: "update:option",
    option: PictureChoiceOption,
    addToHistory?: boolean
  ): void;
  (event: "commit:label"): void;
  (event: "remove"): void;
}>();

const store = useAppStore();
const { t } = useI18n();

const imageFileName = computed(() => {
  const src = props.option.src?.trim();
  if (!src) return "";

  try {
    const decodedPath = decodeURIComponent(new URL(src).pathname);
    return decodedPath.split("/").filter(Boolean).at(-1) ?? "";
  } catch {
    const rawSrc = src.split("?")[0] ?? "";
    let decodedSrc = rawSrc;
    try {
      decodedSrc = decodeURIComponent(rawSrc);
    } catch {
      // Keep malformed legacy paths readable instead of crashing the sidebar.
    }
    return decodedSrc.split("/").filter(Boolean).at(-1) ?? decodedSrc;
  }
});

const emitOption = (option: PictureChoiceOption, addToHistory = true) => {
  emit("update:option", option, addToHistory);
};

const updateLabel = (label: string) => {
  emitOption(
    updatePictureChoiceOptionLabel(cloneDeep(props.option), label),
    false
  );
};

const handleMediaSelection = (data: PictureChoiceMediaSelection) => {
  if (!data?.url) return;

  emitOption(applyPictureChoiceMediaSelection(cloneDeep(props.option), data));
};

const openMediaLibrary = () => {
  openPictureChoiceMediaCenter({
    appHandshake: store.appHandshake,
    subCategory: props.mediaCenterSubCategory,
    onSelect: handleMediaSelection,
  });
};

const removeImage = () => {
  const option = cloneDeep(props.option);
  option.src = "";
  delete option.thumbnailUrl;
  delete option.createdAt;
  emitOption(option);
};
</script>

<template>
  <div :data-testid="`picture-choice-option-row-${index}`" class="py-2">
    <div
      :data-testid="`option-aligned-controls-${index}`"
      class="flex items-center gap-2"
    >
      <Menu05Icon
        v-if="!isOther"
        class="picture-choice-drag-handle h-4 w-4 flex-none cursor-move text-gray-500"
      />
      <div v-else class="h-4 w-4 flex-none" />

      <div class="min-w-0 flex-1 space-y-2">
        <UIInput
          :id="`picture-choice-option-label-${index}`"
          :data-testid="`option-label-${index}`"
          :model-value="option.label"
          :disabled="isOther"
          :placeholder="
            isOther
              ? t('fields.choiceElements.pictureChoice.otherOptionLabel')
              : t('fields.choiceElements.pictureChoice.optionLabel')
          "
          @update:model-value="updateLabel"
          @blur="emit('commit:label')"
        />

        <div :data-testid="`option-media-row-${index}`" class="min-w-0">
          <UIInputGroup>
            <UIInput
              :id="`picture-choice-option-filename-${index}`"
              :data-testid="`option-image-filename-${index}`"
              :model-value="imageFileName"
              :disabled="isOther"
              :placeholder="t('fields.choiceElements.pictureChoice.image')"
              class="min-w-0 flex-1"
              readonly
            >
              <template #suffix>
                <UITooltip
                  v-if="option.src && !isOther"
                  trigger="hover"
                  placement="top"
                >
                  <template #trigger>
                    <UIButton
                      text
                      size="small"
                      :aria-label="
                        t('fields.choiceElements.pictureChoice.removeImage')
                      "
                      @click.stop="removeImage"
                    >
                      <XCloseIcon class="h-4 w-4 text-gray-500" />
                    </UIButton>
                  </template>
                  {{ t("fields.choiceElements.pictureChoice.removeImage") }}
                </UITooltip>
              </template>
            </UIInput>
            <UITooltip trigger="hover" placement="top">
              <template #trigger>
                <UIButton
                  :data-testid="`select-option-image-${index}`"
                  class="min-w-0 flex-none"
                  :disabled="isOther"
                  :aria-label="t('fields.choiceElements.pictureChoice.media')"
                  @click="openMediaLibrary"
                >
                  <ImagePlusIcon class="h-5 w-5" />
                </UIButton>
              </template>
              {{ t("fields.choiceElements.pictureChoice.media") }}
            </UITooltip>
          </UIInputGroup>
        </div>
      </div>

      <div
        v-if="removable"
        :data-testid="`delete-option-control-${index}`"
        class="flex-none self-center"
      >
        <UITooltip trigger="hover" placement="top">
          <template #trigger>
            <UIButton
              :aria-label="
                t('fields.choiceElements.pictureChoice.deleteOption')
              "
              @click="emit('remove')"
            >
              <Trash01Icon class="h-5 w-5" />
            </UIButton>
          </template>
          {{ t("fields.choiceElements.pictureChoice.deleteOption") }}
        </UITooltip>
      </div>
    </div>
  </div>
</template>
